import { useColorMode } from '@chakra-ui/react'
import { Button, Tooltip } from '@missionlabs/react'
import { AddCircleIcon, HelpIcon } from '@missionlabs/react/circleloop'
import { SpeedDial as SpeedDialType } from '@missionlabs/types'
import { SettingsTitle } from 'features/settings/components/SettingsTitle'
import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { SettingsPage } from 'shared/components/settings/SettingsPage'

import { SpeedDialTable } from './SpeedDialTable'

interface Props {
    isAdmin: boolean
    clientSpeedDials: SpeedDialType[]
    userSpeedDials?: SpeedDialType[]
    isLoadingForUser?: boolean
    isLoadingForClient?: boolean
}

export const SpeedDial: FC<Props> = ({
    isAdmin,
    clientSpeedDials,
    userSpeedDials = [],
    isLoadingForUser = false,
    isLoadingForClient = false
}) => {
    const { t } = useTranslation()

    const navigate = useNavigate()

    const { colorMode } = useColorMode()

    const onAddClick = () => {
        navigate(`/${isAdmin ? 'admin' : 'settings'}/speed-dial/add`)
    }

    const titleWithTooltip = useMemo(() => {
        return (
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <span>{t('settings.speedDial.title')}</span>
                <Tooltip
                    label={t('settings.speedDial.overrideHelperText')}
                    shouldWrapChildren={true}
                    placement="bottom"
                >
                    <HelpIcon
                        boxSize="24px"
                        p="4px"
                        color={`${colorMode}.alerts.selectiveYellow`}
                    />
                </Tooltip>
            </div>
        )
    }, [t, colorMode])

    return (
        <SettingsPage
            title={titleWithTooltip}
            subtitle={t('settings.speedDial.subTitle')}
            actions={
                <Button
                    variant="creationary"
                    leftIcon={<AddCircleIcon boxSize="16px" />}
                    onClick={onAddClick}
                >
                    {t('settings.speedDial.createTitle')}
                </Button>
            }
        >
            {isAdmin ? (
                <SpeedDialTable speedDials={clientSpeedDials} isLoading={isLoadingForClient} />
            ) : (
                <>
                    <SettingsTitle label={t('settings.speedDial.userSpeedDials')} divider={false} />
                    <SpeedDialTable
                        speedDials={userSpeedDials}
                        isLoading={isLoadingForUser}
                        maxPerPage={5}
                    />

                    <SettingsTitle
                        label={t('settings.speedDial.clientSpeedDials')}
                        divider={false}
                    />
                    <SpeedDialTable
                        speedDials={clientSpeedDials}
                        isLoading={isLoadingForClient}
                        allowEditing={false}
                        maxPerPage={5}
                    />
                </>
            )}
        </SettingsPage>
    )
}
